import type { Energy } from '@hmawiw/shared';

interface IconProps {
  size?: number;
}

/**
 * A vertical battery with one to three segments lit from the bottom: the energy a card asks
 * for, on the same scale as the level the user sets. Chosen over bars on 2026-09-09.
 */
export function EnergyGlyph({ level, size = 18 }: { level: Energy | 0; size?: number }) {
  const w = Math.round((size * 14) / 18);
  const h = Math.round((size * 26) / 18);
  const off = 0.22;
  return (
    <svg width={w} height={h} viewBox="0 0 14 26" aria-hidden="true">
      <rect x="4.5" y="0" width="5" height="2.5" rx="1" fill="currentColor" />
      <rect x="0.75" y="3.25" width="12.5" height="22" rx="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
      {level === 0 ? (
        // Not set: a question mark in the empty body, not three faint segments.
        <>
          <path d="M4.6 11.2a2.4 2.4 0 1 1 3.6 2.1c-0.9 0.5-1.2 1-1.2 2.2v0.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="7" cy="20.2" r="1.1" fill="currentColor" />
        </>
      ) : (
        <>
          <rect x="3" y="18.5" width="8" height="4.5" rx="1.2" fill="currentColor" opacity={level >= 1 ? 1 : off} />
          <rect x="3" y="12.5" width="8" height="4.5" rx="1.2" fill="currentColor" opacity={level >= 2 ? 1 : off} />
          <rect x="3" y="6.5" width="8" height="4.5" rx="1.2" fill="currentColor" opacity={level >= 3 ? 1 : off} />
        </>
      )}
    </svg>
  );
}

export function Grip({ size = 10 }: IconProps) {
  const h = Math.round(size * 1.6);
  return (
    <svg width={size} height={h} viewBox="0 0 10 16" aria-hidden="true">
      {[2, 8, 14].map((cy) => (
        <g key={cy}>
          <circle cx="2" cy={cy} r="1.5" fill="currentColor" />
          <circle cx="8" cy={cy} r="1.5" fill="currentColor" />
        </g>
      ))}
    </svg>
  );
}

function Stroke({ size = 20, d, width = 2 }: IconProps & { d: string; width?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export const Timer = (p: IconProps) => <Stroke {...p} d="M12 21a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15zM12 9.5v4l2.5 1.5M9.5 3h5M12 3v3M18 6.5l1.5 1.5" />;
export const Repeat = (p: IconProps) => <Stroke {...p} d="M17 2.5l3.5 3.5-3.5 3.5M3.5 12V9.5a3.5 3.5 0 0 1 3.5-3.5h13.5M7 21.5L3.5 18 7 14.5M20.5 12v2.5a3.5 3.5 0 0 1-3.5 3.5H3.5" />;
export const Inbox = (p: IconProps) => <Stroke {...p} d="M3.5 13.5h4.5l1.5 2.5h5l1.5-2.5h4.5M3.5 13.5V19a1.5 1.5 0 0 0 1.5 1.5h14a1.5 1.5 0 0 0 1.5-1.5v-5.5M3.5 13.5l2.5-8a1.5 1.5 0 0 1 1.4-1h9.2a1.5 1.5 0 0 1 1.4 1l2.5 8" />;
export const StepArrow = (p: IconProps) => <Stroke {...p} d="M5 12h13M13 7l5 5-5 5" />;
export const ChevronLeft = (p: IconProps) => <Stroke {...p} d="M15 5l-7 7 7 7" />;
export const ChevronRight = (p: IconProps) => <Stroke {...p} d="M9 5l7 7-7 7" />;
export const ChevronDown = (p: IconProps) => <Stroke {...p} d="M5 9l7 7 7-7" />;
export const ChevronUp = (p: IconProps) => <Stroke {...p} d="M5 15l7-7 7 7" />;
export const Plus = (p: IconProps) => <Stroke {...p} d="M12 5v14M5 12h14" width={2.2} />;
export const Check = (p: IconProps) => <Stroke {...p} d="M5 12l4 4L19 7" width={2.4} />;
export const Cross = (p: IconProps) => <Stroke {...p} d="M6 6l12 12M18 6L6 18" />;
export const Journal = (p: IconProps) => <Stroke {...p} d="M5 4.5h13a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5zM8 4.5v15M11.5 9h4M11.5 12.5h4" />;
export const Alarm = (p: IconProps) => <Stroke {...p} d="M12 21a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM12 10.5V14l2.5 1.5M5 4L2.5 6.5M19 4l2.5 2.5M5.5 20l-1.5 1.5M18.5 20l1.5 1.5" />;
export const Bell = (p: IconProps) => <Stroke {...p} d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4l2-2ZM10 20a2 2 0 0 0 4 0" />;
export const Gear = (p: IconProps) => (
  <svg width={p.size ?? 20} height={p.size ?? 20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
  </svg>
);
